import { computePosition, flip, shift } from "@floating-ui/dom";
import { posToDOMRect, ReactRenderer } from "@tiptap/react";
import { Editor } from "@tiptap/core";
import MentionList from "../../components/MentionList";
import { SuggestionProps, SuggestionRenderProps, User } from "../types/types";

const updatePosition = (editor: Editor, element: HTMLElement): void => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to
      ),
  };

  computePosition(virtualElement, element, {
    placement: "bottom-start",
    strategy: "absolute",
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = "max-content";
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  });
};

export default (users: User[] = []) => {
  const items = ({ query }: { query: string }): User[] => {
    return users.filter((user) =>
      user.name.toLowerCase().includes(query.toLowerCase())
    );
  };

  return {
    items,
    render: (): SuggestionRenderProps => {
      let component: InstanceType<typeof ReactRenderer>;
      return {
        onStart: (props: SuggestionProps) => {
          component = new ReactRenderer(MentionList, {
            props: {
              items: items({ query: props.query }),
              command: props.command,
              editor: props.editor,
              query: props.query,
            },
            editor: props.editor,
          });

          if (!props.clientRect) {
            return;
          }

          const element = component.element as HTMLElement;
          element.style.position = "absolute";
          element.style.zIndex = "100";
          document.body.appendChild(element);
          updatePosition(props.editor, element);
        },

        onUpdate(props: SuggestionProps) {
          component.updateProps({
            items: items({ query: props.query }),
            command: props.command,
            editor: props.editor,
            query: props.query,
          });

          if (!props.clientRect) {
            return;
          }

          updatePosition(props.editor, component.element as HTMLElement);
        },

        onKeyDown({ event }: { event: KeyboardEvent }): boolean {
          if (event.key === "Escape") {
            component.destroy();
            return true;
          }

          const ref = component.ref as {
            onKeyDown?: (props: { event: KeyboardEvent }) => boolean;
          };

          return ref?.onKeyDown?.({ event }) ?? false;
        },

        onExit() {
          component.element.remove();
          component.destroy();
        },
      };
    },
  };
};
